import { z } from "zod";
import {
  ConnectionIdSchema,
  EventCursorSchema,
  EventIdSchema,
  HerSkinRunIdSchema,
  HerSkinSessionIdSchema,
  IsoDateTimeSchema,
  RequestIdSchema,
} from "./common.js";

export const BrowserClientHelloSchema = z
  .object({
    type: z.literal("client.hello"),
    requestId: RequestIdSchema,
    lastCursor: EventCursorSchema.nullable(),
    visibleSessionId: HerSkinSessionIdSchema.nullable(),
    clientVersion: z.string().min(1).max(80),
  })
  .strict();

/** 应用层首选命名；BrowserClientHello 保留为更明确的协议说明名。 */
export const ClientHelloSchema = BrowserClientHelloSchema;

export const ServerWelcomeSchema = z
  .object({
    type: z.literal("server.welcome"),
    connectionId: ConnectionIdSchema,
    serverTime: IsoDateTimeSchema,
    resume: z.enum(["replayed", "snapshot_required", "fresh"]),
    heartbeatMs: z.number().int().min(5_000).max(120_000),
  })
  .strict();

export const AgentEventTypeSchema = z.enum([
  "connection.state_changed",
  "session.created",
  "session.updated",
  "session.snapshot",
  "run.accepted",
  "run.started",
  "run.status_changed",
  "run.activity_updated",
  "run.action_updated",
  "run.completed",
  "run.failed",
  "message.started",
  "message.delta",
  "message.completed",
  "diagnostic.notice",
]);

const GLOBAL_EVENT_TYPES = new Set<z.infer<typeof AgentEventTypeSchema>>([
  "connection.state_changed",
  "diagnostic.notice",
]);

const RUN_EVENT_TYPES = new Set<z.infer<typeof AgentEventTypeSchema>>([
  "run.accepted",
  "run.started",
  "run.status_changed",
  "run.activity_updated",
  "run.action_updated",
  "run.completed",
  "run.failed",
  "message.started",
  "message.delta",
  "message.completed",
]);

export const AgentEventSchema = z
  .object({
    type: AgentEventTypeSchema,
    eventId: EventIdSchema,
    cursor: EventCursorSchema,
    occurredAt: IsoDateTimeSchema,
    receivedAt: IsoDateTimeSchema,
    adapter: z.literal("hermes"),
    sessionId: HerSkinSessionIdSchema.optional(),
    runId: HerSkinRunIdSchema.optional(),
    payload: z.record(z.string(), z.unknown()),
    /** 仅供脱敏诊断；普通 UI 不显示 Hermes 原始方法名。 */
    sourceEvent: z.string().min(1).max(160).optional(),
  })
  .strict()
  .superRefine((event, context) => {
    if (!GLOBAL_EVENT_TYPES.has(event.type) && !event.sessionId) {
      context.addIssue({
        code: "custom",
        path: ["sessionId"],
        message: "Session 级事件必须携带 HerSkin sessionId",
      });
    }

    if (RUN_EVENT_TYPES.has(event.type) && !event.runId) {
      context.addIssue({
        code: "custom",
        path: ["runId"],
        message: "Run/Message 事件必须携带 HerSkin runId",
      });
    }
  });

/** 更明确的公共别名：所有浏览器实时事件都必须使用这一信封。 */
export const AgentEventEnvelopeSchema = AgentEventSchema;

export type BrowserClientHello = z.infer<typeof BrowserClientHelloSchema>;
export type ClientHello = z.infer<typeof ClientHelloSchema>;
export type ServerWelcome = z.infer<typeof ServerWelcomeSchema>;
export type AgentEventType = z.infer<typeof AgentEventTypeSchema>;
export type AgentEvent = z.infer<typeof AgentEventSchema>;
export type AgentEventEnvelope = AgentEvent;
